// only support Observable implementations compliant with the latest tc39 proposal:
// https://github.com/tc39/proposal-observable
import { apiMiddleware as reduxApiMiddleware } from 'redux-api-middleware'
import { once } from '../utils'

/**
 * Configure methods for directly fetching via RSAA, without relying on a redux store's
 * configured apiMiddleware.
 *
 * Generally used for advanced use-cases, where you'd like more control over async
 * action handling for one or more API requests within a context _other_ than the
 * redux-api-middleware instance configured with the redux store (e.g. redux-observable,
 * or redux-saga). Can be used outside of a redux context entirely if necessary.
 *
 * ```
 * type StoreInterface = { getState: Function, dispatch?: Function }
 * type CallRsaaApi = {|
 *   fetchRSAA: (rsaa: RSAA, store?: StoreInterface) => [Promise, FSA],
 *   fromRSAA: (rsaa: RSAA, store?: StoreInterface) => Observable
 * |}
 * ```
 *
 * Example Configuration:
 * ```
 * // use a tc39 compliant Observable implementation (until natively supported) like RxJS
 * import { Observable } from 'rxjs'
 *
 * // Some app middlewares we'd like to use for RSAAs.
 * // These would likely be ones that target the RSAA action type (i.e. isRSAA()).
 * const rsaaMiddleware = [
 *   authMiddleware,
 *   rsaaMetaMiddleware,
 * ]
 *
 * // Middleware that will target the FSAs produced by redux-api-middleware
 * const fsaMiddleware = [
 *   apiRetryMiddleware
 * ]
 *
 * // configure your store... use the same middleware arrays as above if you'd like :)
 * const store = configureStore( ... )
 *
 * // Then create your callRSAA methods using your desired middleware
 * export const { fromRSAA, fetchRSAA } = configureCallRSAA({
 *   Observable,
 *   rsaaMiddleware,
 *   fsaMiddleware,
 *   store
 * })
 * ```
 *
 * Example Use:
 * ```
 * // Returns an array whose first value is a Promise for the `fetch` request, and
 * // whose second value is the "request" FSA.  Promise will resolve the async result
 * // FSA.  If you'd like to dispatch the "request" action before handling the
 * // resolved value, you must do so manually.
 * const rsaa = rsaaCreator({ foo: 'bar' })
 * const [ promise, request ] = fetchRSAA(rsaa)
 * console.log(request)
 * promise.then((result) => {
 *   console.log(result)
 * })
 *
 * // Returns an Observable which will emit the "request" and "success|failure" FSAs to
 * // any subscriptions.  Useful for utilizing rxjs operators that leverage higher order
 * // operators like switchMap, or utils like forkJoin.
 * const testFromRSAA = action$ => action$.pipe(
 *   ofType('TEST_FETCH_RSAA'),
 *   switchMap(() => {
 *     const rsaa1 = rsaaCreator({ foo: 'bar' })
 *     const rsaa2 = rsaaCreator({ woot: 'booyah' })
 *     return forkJoin(
 *       fromRSAA(rsaa1),
 *       fromRSAA(rsaa2)
 *     )
 *   })
 * )
 * ```
 * @alias module:api
 * @param {Observable} Observable tc39 compliant Observable class to use for `fromRSAA`
 * @param {function} [apiMiddleware] override the redux-api-middleware with different implementation (useful for mocks/tests, generally not in production!)
 * @param {function[]} [fsaMiddleware] list of "redux" middleware functions to use for the RSAA's resulting FSAs
 * @param {function} [fsaTransform] custom "transform" to apply to resulting FSAs from called RSAA
 * @param {function[]} [rsaaMiddleware] list of "redux" middleware functions process incoming RSAA
 * @param {function} [rsaaTransform] custom "transform" to apply to incoming RSAA
 * @param {{}} [store] a redux store. leave `dispatch` method undefined if you wish to avoid dispatching action side-effects to store from configured middleware.
 * @return {CALL_RSAA_API} the 2 "Call RSAA" API methods
 */
const configureCallRSAA = (options = {}) => {
  const {
    Observable = defaultObservable(),
    apiMiddleware = reduxApiMiddleware,
    fsaMiddleware = [],
    fsaTransform = n => n,
    rsaaMiddleware = [],
    rsaaTransform = n => n,
    store = {}
  } = options
  const rsaaInterceptor = (rsaa, s = store) => {
    const nextRsaa = reduceMiddleware('rsaa', rsaaMiddleware, rsaa, s)
    return rsaaTransform(nextRsaa, s.getState)
  }
  const fsaInterceptor = (fsa, s = store) => {
    const nextFsa = reduceMiddleware('fsa', fsaMiddleware, fsa, s)
    return fsaTransform(nextFsa, s.getState)
  }
  return {
    fetchRSAA: fetchRSAA(apiMiddleware, rsaaInterceptor, fsaInterceptor),
    fromRSAA: fromRSAA(Observable, apiMiddleware, rsaaInterceptor, fsaInterceptor)
  }
}

export default configureCallRSAA

export const ErrorObservable = Object.freeze({
  create: (observer) => {
    throw new Error('configureCallRSAA: assign Observable class to use fromRSAA.')
  }
})

const fetchRSAA = (middlewareFn, rsaaInterceptor, fsaInterceptor) => (rsaa, store) => {
  const nextRSAA = rsaaInterceptor(rsaa, store)
  let requestAction = null
  const promise = new Promise((resolve, reject) => {
    const next = (action) => {
      if (requestAction) {
        resolve(fsaInterceptor(action, store))
      } else {
        requestAction = fsaInterceptor(action, store)
      }
    }
    const apiFetch = middlewareFn(store)(next)
    apiFetch(nextRSAA).catch(reject)
  })
  return [promise, requestAction]
}

const fromRSAA = (ObservableClass, middlewareFn, rsaaInterceptor, fsaInterceptor) => (rsaa, store) => (
  ObservableClass.create(observer => {
    const nextRSAA = rsaaInterceptor(rsaa, store)
    const next = (action) => observer.next(fsaInterceptor(action, store))
    const apiFetch = middlewareFn(store)(next)

    apiFetch(nextRSAA).then(() => {
      observer.complete()
    }).catch((e) => {
      observer.error(e)
    })
  })
)

function throwDispatchError (action, mwType, index) {
  throw new Error(`configureCallRSAA: configured middleware cannot dispatch action!\n
    Either provide a 'store' configuration, or pass an override to callRSAA method directly.\n
    dispatched: ${action.toString()};\n
    middleware type: ${mwType}\n
    middleware index: ${index};`)
}

// TODO: until I can implement support for async MW functions configured with callRSAA
// methods, I think we should fix this w/ documentation.  Note that any valid redux MW
// _can_ be used for callRSAA config, but only synchronous MWs that immediately send a
// single action to "next" MW (perhaps after a transformation) are supported.  Anything
// else may result in undesired behavior.
// In practice, we generally only include these types of synchronous, transformation
// MWs since we don't really want side effects to occur within this call.  We are
// more likely going to handle side-effects and async processing with another tool,
// like redux-observable, when using this util.
// Maybe use Typed MW function signatures, that can limit the # of acceptable MWs?
// For practical use-cases, these are usually nothing more than a transform, and the
// side-effects are generally going to be handled at the call-site.  Otherwise, the
// MW isn't suited for callRSAA methods, and should only be used as a real, redux MW.
// Possible Type For Simple Transformation and (potential) action side-effect capable MW:
// type RSAANext = RSAA => any
// type FSANext = FSA => any
// type StoreInterface = { getState: () => any, dispatch?: any => any }
// type RSAAMiddleware = StoreInterface => RSAANext => RSAA => any
// type FSAMiddleware = StoreInterface => FSANext => FSA => any
function reduceMiddleware (
  type,
  middleware,
  action,
  store
) {
  const { dispatch, getState } = store
  return middleware.reduce((accAction, curMiddleware, i) => {
    let nextAction = accAction
    const mw = curMiddleware({
      dispatch: dispatch || (a => throwDispatchError(a, type, i)),
      getState
    })
    const next = once(a => { nextAction = a })
    mw(next)(accAction)
    return nextAction
  }, action)
}

function defaultObservable () {
  return ErrorObservable
}
